/**
 * The doc page (slice 2d spec §10.3; boards 48–48g, 51d; Paper's "Doc header ·
 * task pill"): the way back with Edit, Ask to change and Archive or Restore,
 * the title, who changed it and when; the body on a white sheet at letter
 * width, drawn as its blocks (slice 2g); and Linked (its Area, tasks and
 * chats), a column beside it at 1440 and a section under it on a phone.
 * A `doc` event refreshes it while it's read; while it's edited the editor
 * holds the draft (`doc-editor.tsx`). `/docs/new` is New doc's editor.
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { BackLink } from "@crew/ui/components/back-link";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { PageTitle } from "@crew/ui/components/typography";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { Archive, Ellipsis, MessageSquare, Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useNow } from "../../app/now";
import { shortTime } from "../chats/format";
import { panelHref } from "../chats/panel-state";
import { relativeTime } from "../memory/memory-view";
import { taskStatus } from "../tasks/task-view";
import { useArchiveDoc, useDoc, useMoveDoc, useUnlinkDoc, type Doc, type DocLink } from "./api";
import { AreaPicker } from "../areas/area-controls";
import { AreaChip } from "../areas/parts";
import { DocEditView, NewDocEditView } from "./doc-editor";
import { DocBlocks } from "./doc-blocks";
import {
  askHref,
  canEditDoc,
  CHATS_SHOWN,
  linkedSections,
  viaLabel,
  whoName,
  byPhrase,
} from "./doc-view";
import type { DocDraft } from "./doc-view";
import { DocIcon, StageDot, WhoAvatar } from "./parts";
import { FileDocBody } from "../files/file-doc";
import { canDelete, DeleteDoc } from "../files/delete-doc";

// ── The header ──────────────────────────────────────────────────────────────

function AskToChange({
  doc,
  primary = false,
  className,
}: {
  doc: Doc;
  /** The way to change it, when it can't be edited here. */
  primary?: boolean;
  className?: string;
}) {
  const { search } = useLocation();
  return (
    <Link
      href={askHref(search, doc)}
      data-ask-to-change
      className={cn(buttonVariants({ variant: primary ? "default" : "outline" }), className)}
    >
      <MessageSquare />
      Ask to change
    </Link>
  );
}

type Archiving = ReturnType<typeof useArchiveDoc>;

/** Desktop's ⋯: Archive (an archived doc's Restore is its banner's). */
function MoreMenu({ archiving }: { archiving: Archiving }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button variant="outline" size="icon" aria-label="More for this doc" />}
      >
        <Ellipsis />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuItem
          data-doc-action="archive"
          disabled={archiving.isPending}
          onClick={() => archiving.mutate(true)}
        >
          <Archive />
          Archive
          <span className="ml-auto pl-4 text-xs text-muted-foreground">Hides it from Docs</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The phone's ⋯ (48e): the rest of the actions, in a sheet. */
function ActionsSheet({ archiving }: { archiving: Archiving }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="icon"
        aria-label="More for this doc"
        data-doc-actions-button
        onClick={() => setOpen(true)}
      >
        <Ellipsis />
      </Button>
      <Drawer open={open} onOpenChange={setOpen} showSwipeHandle>
        <DrawerContent showCloseButton className="pb-[max(1rem,env(safe-area-inset-bottom))]">
          <DrawerHeader>
            <DrawerTitle>This doc</DrawerTitle>
            <DrawerDescription className="sr-only">What you can do with this doc</DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-1 px-2 pt-3" data-doc-actions>
            <Button
              variant="ghost"
              data-doc-action="archive"
              disabled={archiving.isPending}
              className="h-12 justify-start gap-3 px-3 text-base"
              onClick={() => {
                setOpen(false);
                archiving.mutate(true);
              }}
            >
              <Archive />
              Archive
              <span className="ml-auto text-sm font-normal text-muted-foreground">
                Hides it from Docs
              </span>
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/** 48f: "Archived yesterday by Marcus", with Restore. */
function ArchivedBanner({
  doc,
  me,
  archiving,
  restoreHere,
}: {
  doc: Doc;
  me: Me;
  archiving: Archiving;
  /** At 1440 Restore sits in the banner; on a phone it's in the header. */
  restoreHere: boolean;
}) {
  const now = useNow();
  if (!doc.archived_at) return null;
  const when = relativeTime(doc.archived_at, me.workspace.timezone, now);
  const by = doc.archived_by ? whoName(doc.archived_by, me) : "someone";
  return (
    <div
      role="status"
      data-doc-archived
      className="flex items-center gap-3 rounded-tile bg-secondary px-4 py-2.5 text-sm text-copy"
    >
      <Archive aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        Archived {when} by {by === "You" ? "you" : by}. It's out of Docs and search; nothing was
        deleted
        {canDelete(doc, me) ? ", until you delete it" : ""}.
      </span>
      {restoreHere ? (
        <>
          <DeleteDoc doc={doc} me={me} />
          <Button
            size="sm"
            variant="outline"
            disabled={archiving.isPending}
            onClick={() => archiving.mutate(false)}
          >
            Restore
          </Button>
        </>
      ) : null}
    </div>
  );
}

function ChangedBy({ doc, me, short }: { doc: Doc; me: Me; short: boolean }) {
  const when = shortTime(doc.updated_at, me.workspace.timezone);
  // A phone has no sidebar saying you're in Docs: the byline says it's a doc.
  if (short) {
    return (
      <span
        data-doc-changed-by
        className="flex items-center gap-1.5 text-meta text-muted-foreground"
      >
        <DocIcon className="size-3.5" file={doc.file} />
        <span className="font-medium text-copper">{doc.format === "file" ? "File" : "Doc"}</span>
        <span className="text-hint">·</span>
        {whoName(doc.updated_by, me)} · {when}
      </span>
    );
  }
  return (
    <span data-doc-changed-by className="flex items-center gap-2 text-meta text-muted-foreground">
      <WhoAvatar who={doc.updated_by} size={20} />
      Changed by {byPhrase(doc.updated_by, me)} · {when}
    </span>
  );
}

/** An archived doc's actions in the phone's header (48f): Delete (slice 5b), then Restore. */
function ArchivedActions({ doc, me, archiving }: { doc: Doc; me: Me; archiving: Archiving }) {
  return (
    <>
      <DeleteDoc doc={doc} me={me} size="default" />
      <Button
        variant="outline"
        disabled={archiving.isPending}
        onClick={() => archiving.mutate(false)}
      >
        Restore
      </Button>
    </>
  );
}

function Header({
  doc,
  me,
  mobile,
  archiving,
  onEdit,
}: {
  doc: Doc;
  me: Me;
  mobile: boolean;
  archiving: Archiving;
  onEdit: () => void;
}) {
  const archived = Boolean(doc.archived_at);
  // A doc with blocks is changed by asking, until blocks can be edited well (slice 2g §9).
  // A file doc's text is its file's (slice 5b §4): Replace, on its card, changes it.
  const editable = canEditDoc(doc);
  const edit = editable ? (
    <Button onClick={onEdit} data-doc-edit>
      <Pencil />
      Edit
    </Button>
  ) : null;
  const ask = <AskToChange doc={doc} primary={!editable} />;
  // The doc comes first: the header is the way back, the actions, the title
  // and who changed it. Its Area, task and chats are Linked's.
  if (mobile) {
    return (
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <BackLink href="/docs" className="my-0 mr-auto text-body">
            Docs
          </BackLink>
          {archived ? (
            <ArchivedActions doc={doc} me={me} archiving={archiving} />
          ) : (
            <>
              {ask}
              {edit}
              <ActionsSheet archiving={archiving} />
            </>
          )}
        </div>
        <ArchivedBanner doc={doc} me={me} archiving={archiving} restoreHere={false} />
        <PageTitle
          data-doc-title
          className={cn("text-[34px] leading-10 break-words", archived && "text-muted-foreground")}
        >
          {doc.title}
        </PageTitle>
        <ChangedBy doc={doc} me={me} short />
        {/* Archived, the phone hides Linked (48f): its Area's chip stays here (Devin on #425). */}
        {archived ? <AreaChip area={doc.area} className="self-start" /> : null}
      </header>
    );
  }
  return (
    <header className="flex flex-col gap-3">
      {/* Narrow, beside an open chat, the actions wrap under the way back (Devin on #425). */}
      <div className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-3">
        <BackLink href="/docs" className="mr-auto">
          Docs
        </BackLink>
        {/* Archived, Restore is the banner's: nothing else applies (48f). */}
        {archived ? null : (
          <div className="ml-auto flex items-center gap-2">
            {edit}
            {ask}
            <MoreMenu archiving={archiving} />
          </div>
        )}
      </div>
      <PageTitle data-doc-title className={cn("break-words", archived && "text-muted-foreground")}>
        {doc.title}
      </PageTitle>
      <ChangedBy doc={doc} me={me} short={false} />
      <ArchivedBanner doc={doc} me={me} archiving={archiving} restoreHere />
      {archiving.error ? (
        <p role="alert" className="text-meta text-brick">
          {errorHint(archiving.error)}
        </p>
      ) : null}
    </header>
  );
}

// ── Linked ──────────────────────────────────────────────────────────────────

type Unlinking = ReturnType<typeof useUnlinkDoc>;

/** Removes the link: an × on hover at 1440, a row menu on a phone. */
function UnlinkControl({
  link,
  unlinking,
  mobile,
}: {
  link: DocLink;
  unlinking: Unlinking;
  mobile: boolean;
}) {
  const label = `Unlink ${link.target === "task" ? "task" : "chat"} “${link.title}”`;
  const run = () => unlinking.mutate({ target: link.target, id: link.id });
  if (mobile) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`More for “${link.title}”`}
              className="relative z-10 shrink-0 text-muted-foreground"
            />
          }
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          <DropdownMenuItem data-unlink={link.id} onClick={run} disabled={unlinking.isPending}>
            <X />
            Remove from this doc
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={label}
      data-unlink={link.id}
      disabled={unlinking.isPending}
      onClick={run}
      className="relative z-10 shrink-0 text-muted-foreground opacity-0 group-hover/link:opacity-100 focus-visible:opacity-100"
    >
      <X />
    </Button>
  );
}

const SECTION_LABEL =
  "text-[11px] leading-4 font-semibold tracking-[0.06em] text-muted-foreground uppercase";

/**
 * Its Area (slice 6d spec §4.2), Linked's first section: anyone changes it
 * here. It doesn't follow its chat. An archived doc shows its chip only.
 */
function DocArea({ doc, me }: { doc: Doc; me: Me }) {
  const move = useMoveDoc(doc.id);
  // No Area, and none to pick (or archived): the section has nothing to say.
  if (!doc.area && (doc.archived_at || me.areas.length === 0)) return null;
  return (
    <div data-doc-area className="flex flex-col items-start gap-1">
      <span className={SECTION_LABEL}>Area</span>
      {doc.archived_at ? (
        <AreaChip area={doc.area} />
      ) : (
        <span className="-ml-2">
          <AreaPicker
            me={me}
            current={doc.area ?? null}
            title="Which area is this doc in?"
            line="Just this doc. Its chats and tasks stay where they are."
            onPick={(areaId) => move.mutate(areaId)}
            busy={move.isPending}
            error={move.error}
          />
        </span>
      )}
    </div>
  );
}

function TaskRow({
  link,
  me,
  unlinking,
  mobile,
}: {
  link: DocLink;
  me: Me;
  unlinking: Unlinking;
  mobile: boolean;
}) {
  const s = link.stage ? taskStatus({ stage: link.stage, waiting_on_text: null }) : null;
  const forName = link.for
    ? link.for.id === me.user.id
      ? "you"
      : (link.for.name.split(/\s+/)[0] ?? link.for.name)
    : null;
  const when = link.updated_at ? shortTime(link.updated_at, me.workspace.timezone) : null;
  return (
    <div
      data-linked-task={link.id}
      className="group/link relative flex items-center gap-2 rounded-tile bg-card px-3 py-2.5 shadow-ring"
    >
      <Link
        href={`/tasks/${link.id}`}
        className="flex min-w-0 flex-1 flex-col gap-1 after:absolute after:inset-0"
      >
        <span className="text-sm font-medium text-foreground">{link.title}</span>
        <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          {s && link.stage ? (
            <span className="flex items-center gap-1.5">
              <StageDot stage={link.stage} />
              {s.label}
            </span>
          ) : null}
          <span>{[forName ? `For ${forName}` : null, when].filter(Boolean).join(" · ")}</span>
        </span>
      </Link>
      <UnlinkControl link={link} unlinking={unlinking} mobile={mobile} />
    </div>
  );
}

function ChatRow({
  link,
  me,
  unlinking,
  mobile,
}: {
  link: DocLink;
  me: Me;
  unlinking: Unlinking;
  mobile: boolean;
}) {
  const { search } = useLocation();
  const via = viaLabel(link.via);
  const when = shortTime(link.updated_at ?? link.linked_at, me.workspace.timezone);
  return (
    <div
      data-linked-chat={link.id}
      className="group/link relative flex items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-foreground/5"
    >
      <MessageSquare aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <Link
        href={panelHref(search, link.id)}
        className="flex min-w-0 flex-1 flex-col gap-px after:absolute after:inset-0"
      >
        <span className="truncate text-sm text-foreground">{link.title}</span>
        <span className="text-xs text-muted-foreground">
          {when}
          {via ? ` · ${via}` : null}
        </span>
      </Link>
      <UnlinkControl link={link} unlinking={unlinking} mobile={mobile} />
    </div>
  );
}

type Rows = { me: Me; unlinking: Unlinking; mobile: boolean };

function LinkedTasks({ tasks, ...rows }: { tasks: DocLink[] } & Rows) {
  if (tasks.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <span className={SECTION_LABEL}>Tasks</span>
      {tasks.map((link) => (
        <TaskRow key={link.id} link={link} {...rows} />
      ))}
    </div>
  );
}

/** Chats newest first: five at 1440, three on a phone, then "Show all <n> chats". */
function LinkedChats({ chats, ...rows }: { chats: DocLink[] } & Rows) {
  const [all, setAll] = useState(false);
  if (chats.length === 0) return null;
  const shown = all ? chats : chats.slice(0, rows.mobile ? CHATS_SHOWN.phone : CHATS_SHOWN.wide);
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-baseline justify-between pb-1">
        <span className={SECTION_LABEL}>Chats</span>
        <span className="text-xs text-hint">{chats.length}</span>
      </div>
      {shown.map((link) => (
        <ChatRow key={link.id} link={link} {...rows} />
      ))}
      {chats.length > shown.length ? (
        <button
          type="button"
          data-show-all-chats
          onClick={() => setAll(true)}
          className="w-fit cursor-pointer px-2.5 py-1.5 text-meta font-medium text-foreground underline underline-offset-2"
        >
          Show all {chats.length} chats
        </button>
      ) : null}
    </div>
  );
}

/**
 * Linked (§10.3): its Area, then the doc's real links. Tasks (title, stage, who it's for,
 * when it changed), then Chats with how each came to the doc.
 */
function Linked({ doc, me, mobile }: { doc: Doc; me: Me; mobile: boolean }) {
  const unlinking = useUnlinkDoc(doc.id);
  const { tasks, chats } = linkedSections(doc.links);
  const rows = { me, unlinking, mobile };
  return (
    <section data-doc-linked aria-label="Linked" className="flex min-w-0 flex-col gap-5">
      {mobile ? (
        <h2 className="font-display text-[26px] leading-8 text-foreground">Linked</h2>
      ) : null}
      <DocArea doc={doc} me={me} />
      {doc.links.length === 0 ? (
        <p className="text-meta text-muted-foreground">
          Not linked to a task or a chat. Ask to change links the chat you ask in.
        </p>
      ) : null}
      <LinkedTasks tasks={tasks} {...rows} />
      <LinkedChats chats={chats} {...rows} />
      {unlinking.error ? (
        <p role="alert" className="text-meta text-brick">
          {errorHint(unlinking.error)}
        </p>
      ) : null}
    </section>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

/** The body as read: a white sheet at letter width (48); the page itself on a phone (48d). */
function Body({ doc, mobile }: { doc: Doc; mobile: boolean }) {
  // A file doc's body is its file (slice 5b spec §9.3).
  if (doc.format === "file" && doc.file) return <FileDocBody doc={doc} mobile={mobile} />;
  const archived = Boolean(doc.archived_at);
  const text = doc.body.trim() ? (
    <DocBlocks body={doc.body} />
  ) : (
    <p className="text-body text-muted-foreground">Nothing written yet.</p>
  );
  if (mobile) {
    return (
      <article
        data-doc-body
        className={cn(
          "min-w-0 border-t border-border pt-5 [&_[data-streamdown=table-wrapper]]:-mr-4",
          archived && "opacity-60",
        )}
      >
        {text}
      </article>
    );
  }
  return (
    <article
      data-doc-body
      className={cn(
        "min-w-0 rounded-panel bg-card px-12 py-10 shadow-[0_0_0_1px_rgb(31_42_68/8%),0_8px_24px_rgb(31_42_68/4%)]",
        archived && "opacity-70",
      )}
    >
      {text}
    </article>
  );
}

function ReadView({ doc, me, onEdit }: { doc: Doc; me: Me; onEdit: () => void }) {
  const mobile = useIsMobile();
  const archiving = useArchiveDoc(doc.id);
  if (mobile) {
    return (
      <main data-doc-page={doc.id} className="flex w-full min-w-0 flex-col gap-5 px-4 pt-4 pb-16">
        <Header doc={doc} me={me} mobile archiving={archiving} onEdit={onEdit} />
        <Body doc={doc} mobile />
        {doc.archived_at ? null : (
          <div className="border-t border-border pt-5">
            <Linked doc={doc} me={me} mobile />
          </div>
        )}
        {archiving.error ? (
          <p role="alert" className="text-meta text-brick">
            {errorHint(archiving.error)}
          </p>
        ) : null}
      </main>
    );
  }
  return (
    // Linked sits beside the doc when there's room for both (1440 with the panel
    // collapsed), else under it (the chat panel open beside the page, 51d).
    <main
      data-doc-page={doc.id}
      className="@container w-full min-w-0 px-6 pt-8 pb-16 md:px-12 md:pt-9"
    >
      <div className="mx-auto grid w-full max-w-letter grid-cols-1 gap-x-10 gap-y-6 @min-[66rem]:max-w-[1040px] @min-[66rem]:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0 @min-[66rem]:col-start-1">
          <Header doc={doc} me={me} mobile={false} archiving={archiving} onEdit={onEdit} />
        </div>
        <div className="min-w-0 @min-[66rem]:col-start-1 @min-[66rem]:row-start-2">
          <Body doc={doc} mobile={false} />
        </div>
        <aside className="min-w-0 @min-[66rem]:col-start-2 @min-[66rem]:row-start-2">
          <Linked doc={doc} me={me} mobile={false} />
        </aside>
      </div>
    </main>
  );
}

/** New doc's typing during its first Save, handed over in the history entry. */
function useCarriedDraft() {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [carried, setCarried] = useState(
    () => (state as { draft?: DocDraft | null } | null)?.draft ?? undefined,
  );
  // Taken once: a reload or Back later reads the doc, not a draft long saved.
  useEffect(() => {
    if (carried) void navigate(pathname, { replace: true, state: null });
  }, [carried, navigate, pathname]);
  return [carried, () => setCarried(undefined)] as const;
}

function LoadedDoc({ doc, me }: { doc: Doc; me: Me }) {
  const [carried, dropCarried] = useCarriedDraft();
  const [editing, setEditing] = useState(Boolean(carried));
  // The carried draft seeds only the editor it opened: once that editor
  // closes (Save or Discard), a later Edit starts from the doc (Codex on #275).
  const close = () => {
    setEditing(false);
    dropCarried();
  };
  // Archived, there's nothing to edit (`409 doc_archived`): the page reads. An
  // editor already open stays open if the doc gains blocks meanwhile: the
  // person's draft is theirs to save or discard.
  if (editing && !doc.archived_at) {
    return <DocEditView doc={doc} me={me} onClose={close} carried={carried} />;
  }
  return <ReadView doc={doc} me={me} onEdit={() => setEditing(true)} />;
}

export function DocPage({ docId, me }: { docId: string; me: Me }) {
  const doc = useDoc(docId);
  if (doc.data) return <LoadedDoc doc={doc.data} me={me} />;
  if (doc.error) {
    const gone = doc.error instanceof ApiError && doc.error.status === 404;
    return (
      <main className="flex flex-col gap-3 px-4 pt-10 sm:px-6 md:px-10">
        <p className="text-body text-muted-foreground">
          {gone ? "This doc isn't in your workspace." : errorHint(doc.error)}
        </p>
        <Link href="/docs" className="text-meta text-foreground underline underline-offset-2">
          All docs
        </Link>
      </main>
    );
  }
  return (
    <div className="flex flex-1 items-center justify-center py-20 text-muted-foreground">
      <Spinner className="size-5" />
    </div>
  );
}

/** `/docs/new?title=&task=&area=`: New doc's editor, until its first Save makes the doc. */
export function NewDocPage({ me }: { me: Me }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const title = params.get("title")?.trim() || "Untitled";
  const taskId = params.get("task") ?? undefined;
  const areaId = params.get("area") ?? undefined;
  return (
    <NewDocEditView
      title={title}
      taskId={taskId}
      areaId={areaId}
      me={me}
      onDiscard={() => navigate(taskId ? `/tasks/${taskId}` : "/docs", { replace: true })}
    />
  );
}
